import { type ComponentProps, useCallback } from 'react';
import { useRouter } from 'expo-router';

import { type OrderLineItem } from '@order/features/orders';

// ─────────────────────────────────────────────────────────────────────────────

export const useNavigateToReorderCustomization = (params?: Params) => {
  const { shouldUseModalNavigation, shouldReplaceScreen = false } = params ?? {};

  const router = useRouter();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const navigateToCustomizationScreen = useCallback<
    NonNullable<OrderLineItemProps['onLineItemCardPress']>
  >(
    (details) => {
      const { lineItemId, addressId, restaurantSlug, customName, product } = details;
      const productSlug = product?.slug ?? '';

      if (addressId) {
        router.navigate({
          pathname: '/delivery/[addressId]/[productSlug]',
          params: {
            addressId,
            productSlug,
            reorderLineItemId: lineItemId,
            ...(customName ? { name: customName } : {}),
          },
        });
        return;
      }

      router.navigate({
        pathname: '/[restaurantSlug]/[productSlug]',
        params: {
          restaurantSlug,
          productSlug,
          reorderLineItemId: lineItemId,
          ...(customName ? { name: customName } : {}),
        },
      });
    },
    [router],
  );

  const navigateToCustomizationModal = useCallback<
    NonNullable<OrderLineItemProps['onLineItemCardPress']>
  >(
    (details) => {
      const { lineItemId, addressId, restaurantSlug, customName, product } = details;
      const productSlug = product?.slug ?? '';

      if (addressId) {
        const routeParams = {
          pathname: '/delivery/[addressId]/[productSlug]' as const,
          params: {
            addressId,
            productSlug,
            reorderLineItemId: lineItemId,
            ...(customName ? { name: customName } : {}),
          },
        };

        if (shouldReplaceScreen) {
          router.replace(routeParams);
        } else {
          router.navigate(routeParams);
        }

        return;
      }

      const routeParams = {
        pathname: '/[restaurantSlug]/[productSlug]' as const,
        params: {
          restaurantSlug,
          productSlug,
          reorderLineItemId: lineItemId,
          ...(customName ? { name: customName } : {}),
        },
      };

      if (shouldReplaceScreen) {
        router.replace(routeParams);
      } else {
        router.navigate(routeParams);
      }
    },
    [router, shouldReplaceScreen],
  );

  // ─────────────────────────────────────────────────────────────────────

  return useCallback<NonNullable<OrderLineItemProps['onLineItemCardPress']>>(
    (details) => {
      if (shouldUseModalNavigation) {
        navigateToCustomizationModal(details);

        return;
      }

      navigateToCustomizationScreen(details);
    },
    [navigateToCustomizationModal, navigateToCustomizationScreen, shouldUseModalNavigation],
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  shouldUseModalNavigation: boolean;
  shouldReplaceScreen?: boolean;
};

type OrderLineItemProps = ComponentProps<typeof OrderLineItem>;
